/**
 * Negotiation facts card — surfaces all key fields from the detail record via the
 * shared InfoGrid. Mirrors the legacy detail info grid: code, type, stage, status,
 * priority, customer, creator, assignees, value, progress, deadline, journey step,
 * content, notes. Uses --vn-* tokens only; no hand-rolled layout.
 */
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, CardTitle, Chip, Progress } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { formatDate, formatMoney } from '@/lib/format';
import type { ContractNegotiationDetail } from '@/api/contract-negotiations';
import {
  negotiationStatusTone,
  negotiationStatusLabel,
  negotiationPriorityTone,
  negotiationPriorityLabel,
  negotiationTypeLabel,
  negotiationStageLabel,
} from '../negotiation-format';

export function NegotiationInfoCard({ detail }: { detail: ContractNegotiationDetail }) {
  const assignees = detail.assignee_names ?? [];

  const items: InfoItem[] = [
    { label: 'Code', value: detail.contract_negotiation_code },
    {
      label: 'Status',
      value: (
        <StatusPill tone={negotiationStatusTone(detail.contract_negotiation_status)}>
          {negotiationStatusLabel(detail.contract_negotiation_status)}
        </StatusPill>
      ),
    },
    {
      label: 'Priority',
      value: detail.contract_negotiation_priority ? (
        <Chip appearance="subtle" tone={negotiationPriorityTone(detail.contract_negotiation_priority)} size="sm">
          {negotiationPriorityLabel(detail.contract_negotiation_priority)}
        </Chip>
      ) : null,
    },
    { label: 'Type', value: negotiationTypeLabel(detail.contract_negotiation_type) },
    { label: 'Stage', value: negotiationStageLabel(detail.contract_negotiation_stage ?? undefined) },
    {
      label: 'Customer',
      value: detail.contract_negotiation_customer_id ? (
        <Link
          to={`/customers/${detail.contract_negotiation_customer_id}`}
          style={{ color: 'var(--vn-primary-text)' }}
        >
          {detail.customer_name ?? `#${detail.contract_negotiation_customer_id}`}
        </Link>
      ) : (detail.customer_name ?? null),
    },
    { label: 'Creator', value: detail.creator_name },
    {
      label: 'Assignees',
      value: assignees.length > 0 ? assignees.join(', ') : null,
    },
    { label: 'Value', value: formatMoney(detail.contract_negotiation_value) },
    {
      label: 'Progress',
      value: (() => {
        const pct = detail.contract_negotiation_progress ?? 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <Progress value={pct} max={100} style={{ flex: 1, minWidth: 80 }} />
            <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>{pct}%</span>
          </div>
        );
      })(),
    },
    { label: 'Deadline', value: formatDate(detail.contract_negotiation_deadline) },
    { label: 'Created', value: formatDate(detail.created_at) },
    { label: 'Updated', value: formatDate(detail.updated_at) },
    {
      label: 'Journey Step',
      value: detail.contract_negotiation_customer_journey_step ?? null,
    },
  ];

  if (detail.contract_negotiation_content) {
    items.push({
      label: 'Content',
      value: (
        <span style={{ whiteSpace: 'pre-wrap' }}>{detail.contract_negotiation_content}</span>
      ),
      full: true,
    });
  }

  if (detail.contract_negotiation_notes) {
    items.push({
      label: 'Notes',
      value: (
        <span style={{ whiteSpace: 'pre-wrap' }}>{detail.contract_negotiation_notes}</span>
      ),
      full: true,
    });
  }

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Negotiation Details</CardTitle>
      </CardHeader>
      <CardBody>
        <InfoGrid items={items} />
      </CardBody>
    </Card>
  );
}
